import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  TrendingUp,
  Zap,
  CheckCircle,
  ArrowRight,
  Star,
  Sliders,
  Car,
  Users,
  Coins,
  Lock,
  Clock,
  ChevronDown,
  HelpCircle,
  Mail,
  Percent,
  Award,
  BadgeCheck,
  Building,
  Sparkles,
} from "lucide-react";
import ResponsiveAppBar from "../components/AppBar";
import Footer from "../components/Footer";

const LandingPage = () => {
  const navigate = useNavigate();

  // Card Switcher State
  const [currentCard, setCurrentCard] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Live Mini EMI Calculator State on Homepage
  const [emiAmount, setEmiAmount] = useState(200000);
  const [emiRate, setEmiRate] = useState(14);
  const [emiTenure, setEmiTenure] = useState(24);

  // FAQ Accordion State
  const [openFaq, setOpenFaq] = useState(null);

  const cardData = [
    {
      title: "Gold Loan (Jewel)",
      amount: "Up to ₹10,00,000",
      badge: "Fastest Disbursal",
      icon: Coins,
      gradient: "from-[#936097] via-[#854d8a] to-[#77c8c7]",
      details: [
        { label: "Interest Rate", value: "Starting from 1.2% / mo" },
        { label: "Processing Time", value: "Under 30 Minutes" },
        { label: "Asset Storage", value: "Insured Bank Vaults" },
      ],
      path: "/gold-loan",
    },
    {
      title: "Vehicle Loan",
      amount: "Up to ₹15,00,000",
      badge: "Up to 90% Funding",
      icon: Car,
      gradient: "from-[#77c8c7] via-[#5fb8b7] to-[#936097]",
      details: [
        { label: "Vehicles", value: "2-Wheelers & 4-Wheelers" },
        { label: "Interest Rate", value: "Competitive 12.8% p.a." },
        { label: "Tenure", value: "Up to 60 Months" },
      ],
      path: "/vehicle-loan",
    },
    {
      title: "Chit & Savings Fund",
      amount: "₹1,00,000 - ₹5,00,000",
      badge: "High Yield Savings",
      icon: Users,
      gradient: "from-[#936097] via-[#77c8c7] to-[#936097]",
      details: [
        { label: "Monthly Deposit", value: "Customized Schemes" },
        { label: "Duration", value: "12 to 25 Months" },
        { label: "Transparency", value: "100% Regulated" },
      ],
      path: "/contact",
    },
  ];

  const handleCardClick = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentCard((prev) => (prev + 1) % cardData.length);
      setIsTransitioning(false);
    }, 300);
  };

  const current = cardData[currentCard];
  const next = cardData[(currentCard + 1) % cardData.length];

  // Calculate live mini EMI
  const calculateLiveEMI = () => {
    const monthlyRate = emiRate / 100 / 12;
    const emi =
      (emiAmount * monthlyRate * Math.pow(1 + monthlyRate, emiTenure)) /
      (Math.pow(1 + monthlyRate, emiTenure) - 1);
    return Math.round(emi);
  };

  const liveEmi = calculateLiveEMI();
  const totalPayable = liveEmi * emiTenure;
  const totalInterest = totalPayable - emiAmount;

  const faqs = [
    {
      q: "What documents are required to apply for a Gold Loan?",
      a: "Only a basic government-issued ID proof (Aadhaar Card, Voter ID, or Driving License) and PAN Card along with your gold jewellery. No income certificate or CIBIL score checks are needed.",
    },
    {
      q: "How safe is my pledged gold jewellery?",
      a: "Your gold is stored in high-security, fireproof bank-grade vaults with 24/7 CCTV surveillance, tamper-evident sealing, and full insurance coverage during the entire loan tenure.",
    },
    {
      q: "How quickly will I receive my loan amount?",
      a: "Gold loans are disbursed within 20 to 30 minutes after on-the-spot electronic purity testing. Vehicle loans are typically approved and disbursed within 24 to 48 hours.",
    },
    {
      q: "Can I prepay or close my loan before the agreed tenure?",
      a: "Yes! You can close or make partial repayments anytime with zero foreclosure penalty fees, paying interest only for the exact days you used the funds.",
    },
    {
      q: "What types of vehicles can be financed?",
      a: "We offer tailored financing for both brand new and certified pre-owned two-wheelers, four-wheelers, auto-rickshaws, and light commercial vehicles.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 font-sans selection:bg-[#77c8c7] selection:text-white">
      <ResponsiveAppBar />

      {/* ================= HERO SECTION ================= */}
      <section className="relative overflow-hidden pt-12 pb-24 lg:pt-20 lg:pb-32 bg-gradient-to-b from-white via-[#f8fafc] to-[#eff6ff] text-slate-900 border-b border-slate-200/60">
        {/* Glowing Background Mesh Orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#936097]/15 rounded-full filter blur-[120px] pointer-events-none animate-pulse" />
        <div className="absolute bottom-10 right-1/4 w-[28rem] h-[28rem] bg-[#77c8c7]/20 rounded-full filter blur-[140px] pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">
          {/* Trust Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/80 backdrop-blur-md border border-[#936097]/20 text-sm font-semibold text-[#936097] shadow-sm hover:scale-105 transition-transform duration-300">
              <Sparkles className="w-4 h-4 text-[#77c8c7] animate-spin" />
              <span>Tamil Nadu's Trusted Finance Partner</span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#77c8c7]" />
              <span className="text-slate-700 font-medium">1000+ Happy Customers</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.15] text-slate-900">
                Financial Solutions at{" "}
                <span className="bg-gradient-to-r from-[#77c8c7] via-[#936097] to-[#77c8c7] bg-clip-text text-transparent">
                  Market-Best Rates.
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-normal">
                Unlock instant financial liquidity with our premier <strong>Gold Loans</strong>, flexible <strong>Vehicle Financing</strong>, and customized repayment schedules. Zero hidden charges, transparent processing, same-day cash disbursal.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
                <button
                  onClick={() => navigate("/loan-selection")}
                  className="w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-white bg-gradient-to-r from-[#936097] to-[#77c8c7] hover:from-[#7e4d82] hover:to-[#5eb8b7] shadow-lg shadow-[#936097]/25 transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center gap-2 group cursor-pointer"
                >
                  <span>Apply For Loan</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform" />
                </button>

                <button
                  onClick={() => navigate("/emi")}
                  className="w-full sm:w-auto px-8 py-4 rounded-xl font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 shadow-sm transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Percent className="w-4 h-4 text-[#936097]" />
                  <span>Calculate EMI</span>
                </button>
              </div>

              {/* Bullet Features Under Hero */}
              <div className="pt-6 grid grid-cols-2 sm:grid-cols-3 gap-4 text-left border-t border-slate-200">
                <div className="flex items-center gap-2 text-slate-700 text-sm font-medium">
                  <BadgeCheck className="w-5 h-5 text-[#0d9488] shrink-0" />
                  <span>30-Min Disbursal</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700 text-sm font-medium">
                  <Shield className="w-5 h-5 text-[#936097] shrink-0" />
                  <span>Insured Vaults</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700 text-sm font-medium col-span-2 sm:col-span-1">
                  <Award className="w-5 h-5 text-[#0d9488] shrink-0" />
                  <span>Max Gold Value</span>
                </div>
              </div>
            </div>

            {/* Right Interactive 3D Card Stack */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative w-full max-w-md">
                {/* Decorative glow box */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#936097]/40 to-[#77c8c7]/40 rounded-3xl filter blur-2xl transform scale-95" />

                {/* Background Back Card for 3D depth */}
                <div
                  className={`absolute -top-3 -right-3 w-full h-full rounded-2xl bg-gradient-to-br ${next.gradient} opacity-40 transform rotate-3 transition-all duration-500`}
                />

                {/* Foreground Active Card */}
                <div
                  onClick={handleCardClick}
                  className={`relative z-10 w-full rounded-2xl bg-gradient-to-br ${current.gradient} p-7 text-white shadow-2xl cursor-pointer select-none transition-all duration-300 hover:scale-[1.02] border border-white/20 ${
                    isTransitioning ? "scale-95 opacity-70" : ""
                  }`}
                >
                  {/* Card Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-semibold uppercase tracking-wider mb-2 text-white">
                        {current.badge}
                      </span>
                      <h3 className="text-2xl font-bold">{current.title}</h3>
                      <div className="text-3xl font-extrabold mt-1 tracking-tight text-white drop-shadow">
                        {current.amount}
                      </div>
                    </div>
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-inner">
                      <current.icon className="w-7 h-7 text-white" />
                    </div>
                  </div>

                  {/* Card Details */}
                  <div className="space-y-3 bg-black/15 backdrop-blur-sm rounded-xl p-4 border border-white/10 mb-6">
                    {current.details.map((detail, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-white/80">{detail.label}</span>
                        <span className="font-semibold text-white">{detail.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Card Action */}
                  <div className="flex items-center justify-between pt-2 border-t border-white/15 text-xs text-white/90">
                    <span className="flex items-center gap-1.5 font-medium">
                      <Clock className="w-3.5 h-3.5" /> Tap card to rotate loans
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(current.path);
                      }}
                      className="px-4 py-1.5 bg-white text-[#936097] rounded-lg font-bold text-xs hover:bg-slate-100 transition-colors shadow"
                    >
                      {current.path === "/contact" ? "Contact Us →" : "Apply Now →"}
                    </button>
                  </div>
                </div>

                {/* Progress Indicator dots */}
                <div className="flex justify-center gap-2 mt-5">
                  {cardData.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentCard(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === currentCard ? "w-8 bg-[#77c8c7]" : "w-2 bg-white/30"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= STATS SECTION ================= */}
      <section className="relative z-20 -mt-10 container mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {[
            {
              value: "₹25+ Crores",
              label: "Total Loans Disbursed",
              icon: TrendingUp,
              color: "text-[#77c8c7]",
              border: "border-l-4 border-[#77c8c7]",
            },
            {
              value: "99.2%",
              label: "Fast Approval Rate",
              icon: Zap,
              color: "text-[#936097]",
              border: "border-l-4 border-[#936097]",
            },
            {
              value: "30 Mins",
              label: "Gold Loan Processing",
              icon: Clock,
              color: "text-[#77c8c7]",
              border: "border-l-4 border-[#77c8c7]",
            },
            {
              value: "1,200+",
              label: "Happy Active Customer",
              icon: Users,
              color: "text-[#936097]",
              border: "border-l-4 border-[#936097]",
            },
          ].map((stat, i) => (
            <div
              key={i}
              className={`bg-white rounded-2xl p-6 shadow-xl shadow-slate-200/50 border border-slate-100 ${stat.border} hover:-translate-y-1 transition-transform duration-300`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`text-2xl sm:text-3xl font-extrabold ${stat.color}`}>
                  {stat.value}
                </span>
                <stat.icon className={`w-6 h-6 ${stat.color} opacity-80`} />
              </div>
              <p className="text-slate-600 font-medium text-xs sm:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= OUR LOAN PRODUCTS ================= */}
      <section className="py-20 lg:py-28 container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#936097]/15 text-[#936097] text-xs font-bold uppercase tracking-wider mb-3 border border-[#936097]/30">
            Tailored Financial Products
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Explore Our Comprehensive Loan Services
          </h2>
          <p className="text-slate-600 text-base sm:text-lg mt-3">
            Choose from our specialized loan options designed for maximum convenience, high value, and ultra-competitive rates.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Card 1: Gold Loan */}
          <div className="group bg-white rounded-3xl p-8 border border-slate-200/80 shadow-lg hover:shadow-2xl hover:border-[#936097]/50 transition-all duration-300 flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-[#936097]/15 flex items-center justify-center text-[#936097] mb-6 group-hover:scale-110 transition-transform">
                <Coins className="w-7 h-7" />
              </div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-2xl font-bold text-slate-900">Gold Loan</h3>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#936097]/15 text-[#936097] border border-[#936097]/30">
                  Most Popular
                </span>
              </div>
              <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                Avail instant cash against your gold ornaments with maximum valuation per gram and zero appraisal charges.
              </p>

              <ul className="space-y-3 mb-8 text-sm text-slate-700">
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-[#77c8c7] shrink-0" />
                  <span>Up to 75%-80% of gold market value</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-[#77c8c7] shrink-0" />
                  <span>Instant disbursal in 20-30 minutes</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-[#77c8c7] shrink-0" />
                  <span>Safe locker custody with full insurance</span>
                </li>
              </ul>
            </div>

            <button
              onClick={() => navigate("/gold-loan")}
              className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-[#936097] to-[#77c8c7] text-white font-bold text-sm hover:opacity-95 shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Apply for Gold Loan</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Card 2: Vehicle Loan */}
          <div className="group bg-white rounded-3xl p-8 border border-slate-200/80 shadow-lg hover:shadow-2xl hover:border-[#77c8c7]/50 transition-all duration-300 flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-[#77c8c7]/15 flex items-center justify-center text-[#77c8c7] mb-6 group-hover:scale-110 transition-transform">
                <Car className="w-7 h-7" />
              </div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-2xl font-bold text-slate-900">Vehicle Loan</h3>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#77c8c7]/15 text-[#0f766e] border border-[#77c8c7]/30">
                  Easy EMI
                </span>
              </div>
              <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                Drive home your dream bike, scooter, or car with our budget-friendly vehicle loan finance options.
              </p>

              <ul className="space-y-3 mb-8 text-sm text-slate-700">
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-[#77c8c7] shrink-0" />
                  <span>Up to 90% on-road funding available</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-[#77c8c7] shrink-0" />
                  <span>Tenures spanning 12 to 60 months</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-[#77c8c7] shrink-0" />
                  <span>Minimal documentation & quick sanction</span>
                </li>
              </ul>
            </div>

            <button
              onClick={() => navigate("/vehicle-loan")}
              className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-[#77c8c7] to-[#936097] text-white font-bold text-sm hover:opacity-95 shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Apply for Vehicle Loan</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Card 3: Personalized Financial Support */}
          <div className="group bg-white rounded-3xl p-8 border border-slate-200/80 shadow-lg hover:shadow-2xl hover:border-[#936097]/50 transition-all duration-300 flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-[#936097]/15 flex items-center justify-center text-[#936097] mb-6 group-hover:scale-110 transition-transform">
                <Building className="w-7 h-7" />
              </div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-2xl font-bold text-slate-900">Chit & Schemes</h3>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#936097]/15 text-[#936097] border border-[#936097]/30">
                  Savings Scheme
                </span>
              </div>
              <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                Build disciplined savings or bid for immediate funding through our legally compliant chit fund plans.
              </p>

              <ul className="space-y-3 mb-8 text-sm text-slate-700">
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-[#77c8c7] shrink-0" />
                  <span>High dividend earnings on monthly contributions</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-[#77c8c7] shrink-0" />
                  <span>Emergency bidding access for capital needs</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-[#77c8c7] shrink-0" />
                  <span>Transparent online accounts tracking</span>
                </li>
              </ul>
            </div>

            <button
              onClick={() => navigate("/contact")}
              className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-[#936097] to-[#77c8c7] text-white font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Contact Us</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ================= LIVE INTERACTIVE EMI CALCULATOR ================= */}
      <section className="py-20 bg-gradient-to-br from-[#f8fafc] via-[#f1f5f9] to-[#eff6ff] text-slate-900 relative overflow-hidden border-y border-slate-200/70">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left Controls */}
            <div className="lg:col-span-7 space-y-6">
              <span className="inline-block px-3.5 py-1 rounded-full bg-[#77c8c7]/15 text-[#0f766e] text-xs font-bold uppercase tracking-wider border border-[#77c8c7]/30">
                Financial Planning Tool
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                Calculate Your Monthly EMI Instantly
              </h2>
              <p className="text-slate-600 text-base">
                Adjust the sliders below to estimate your monthly installment and total repayment cost before you apply.
              </p>

              {/* Slider 1: Loan Amount */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-3">
                <div className="flex justify-between items-center text-sm font-semibold">
                  <span className="text-slate-700">Loan Amount:</span>
                  <span className="text-[#0d9488] text-lg font-bold">
                    ₹{emiAmount.toLocaleString("en-IN")}
                  </span>
                </div>
                <input
                  type="range"
                  min="20000"
                  max="1000000"
                  step="10000"
                  value={emiAmount}
                  onChange={(e) => setEmiAmount(Number(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#77c8c7]"
                />
                <div className="flex justify-between text-xs text-slate-500 font-medium">
                  <span>₹20,000</span>
                  <span>₹10,00,000</span>
                </div>
              </div>

              {/* Slider 2: Interest Rate */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-3">
                <div className="flex justify-between items-center text-sm font-semibold">
                  <span className="text-slate-700">Interest Rate (% per annum):</span>
                  <span className="text-[#936097] text-lg font-bold">{emiRate}%</span>
                </div>
                <input
                  type="range"
                  min="8"
                  max="24"
                  step="0.5"
                  value={emiRate}
                  onChange={(e) => setEmiRate(Number(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#936097]"
                />
                <div className="flex justify-between text-xs text-slate-500 font-medium">
                  <span>8% p.a.</span>
                  <span>24% p.a.</span>
                </div>
              </div>

              {/* Slider 3: Loan Tenure */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-3">
                <div className="flex justify-between items-center text-sm font-semibold">
                  <span className="text-slate-700">Loan Tenure:</span>
                  <span className="text-[#0d9488] text-lg font-bold">{emiTenure} Months</span>
                </div>
                <input
                  type="range"
                  min="6"
                  max="60"
                  step="6"
                  value={emiTenure}
                  onChange={(e) => setEmiTenure(Number(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#77c8c7]"
                />
                <div className="flex justify-between text-xs text-slate-500 font-medium">
                  <span>6 Months</span>
                  <span>60 Months</span>
                </div>
              </div>
            </div>

            {/* Right Result Card */}
            <div className="lg:col-span-5">
              <div className="bg-white border border-slate-200/80 rounded-3xl p-8 shadow-xl shadow-slate-200/60 space-y-6">
                <div className="text-center pb-6 border-b border-slate-100">
                  <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                    Estimated Monthly EMI
                  </span>
                  <div className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#936097] to-[#0d9488] mt-2">
                    ₹{liveEmi.toLocaleString("en-IN")}
                    <span className="text-sm text-slate-500 font-normal"> /mo</span>
                  </div>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                    <span className="text-slate-600 font-medium">Principal Amount</span>
                    <span className="font-bold text-slate-900">
                      ₹{emiAmount.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="flex justify-between p-3.5 rounded-xl bg-teal-50/50 border border-teal-100/60">
                    <span className="text-slate-600 font-medium">Total Interest Payable</span>
                    <span className="font-bold text-[#0d9488]">
                      ₹{Math.max(0, totalInterest).toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="flex justify-between p-3.5 rounded-xl bg-purple-50/50 border border-purple-100/60">
                    <span className="text-slate-600 font-medium">Total Amount Payable</span>
                    <span className="font-bold text-[#936097]">
                      ₹{totalPayable.toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    onClick={() => navigate("/emi")}
                    className="w-full py-4 rounded-xl font-bold bg-gradient-to-r from-[#936097] to-[#77c8c7] text-white hover:opacity-95 shadow-md shadow-[#936097]/25 transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <span>View Detailed Amortization Table</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 4 PILLARS OF TRUST ================= */}
      <section className="py-20 lg:py-28 container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#77c8c7]/15 text-[#0d9488] text-xs font-bold uppercase tracking-wider mb-3 border border-[#77c8c7]/30">
            Why Velavan Finance
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Built on Transparency, Speed & Trust
          </h2>
          <p className="text-slate-600 text-base sm:text-lg mt-3">
            We operate with the highest professional ethics, ensuring your assets and personal data remain protected at all times.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            {
              icon: Zap,
              title: "Lightning Fast",
              desc: "Walk in with collateral and leave with cash or direct bank transfer in less than 30 minutes.",
              color: "text-[#77c8c7]",
              bg: "bg-[#77c8c7]/15",
            },
            {
              icon: Lock,
              title: "Bank-Grade Vaults",
              desc: "Certified safe deposit lockers with multi-tier insurance and continuous CCTV surveillance.",
              color: "text-[#936097]",
              bg: "bg-[#936097]/15",
            },
            {
              icon: Percent,
              title: "Lowest Interest Rates",
              desc: "Transparent rates from 1.2% per month with zero hidden processing or appraisal fees.",
              color: "text-[#77c8c7]",
              bg: "bg-[#77c8c7]/15",
            },
            {
              icon: Sliders,
              title: "Flexible Repayment",
              desc: "Repay interest monthly or close in one shot whenever you choose without early closure penalty.",
              color: "text-[#936097]",
              bg: "bg-[#936097]/15",
            },
          ].map((feature, idx) => (
            <div
              key={idx}
              className="bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300"
            >
              <div
                className={`w-14 h-14 rounded-2xl ${feature.bg} flex items-center justify-center ${feature.color} mb-6`}
              >
                <feature.icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= HOW IT WORKS (3 STEPS) ================= */}
      <section className="py-20 bg-slate-100/70 border-y border-slate-200">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#936097] px-3 py-1 rounded-full bg-[#936097]/10 border border-[#936097]/20">
              Simple 3-Step Process
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3">
              Getting Your Loan is Fast & Easy
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {[
              {
                step: "01",
                title: "Apply Online or Inquire",
                desc: "Fill our quick application online for gold and vehicle loans, or contact us directly for chit schemes.",
              },
              {
                step: "02",
                title: "Instant Asset Valuation",
                desc: "Our certified evaluators assess your gold or vehicle on the spot using standard tests.",
              },
              {
                step: "03",
                title: "Instant Fund Disbursal",
                desc: "Sign simple paperwork and receive the loan amount directly via Cash, UPI, or NEFT/IMPS.",
              },
            ].map((step, idx) => (
              <div
                key={idx}
                className="bg-white rounded-3xl p-8 border border-slate-200 shadow-md relative overflow-hidden"
              >
                <span className="text-6xl font-black text-slate-100 absolute -top-2 right-4 select-none">
                  {step.step}
                </span>
                <div className="relative z-10">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#936097] to-[#77c8c7] text-white font-bold flex items-center justify-center mb-5 text-sm shadow">
                    {idx + 1}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CUSTOMER TESTIMONIALS ================= */}
      <section className="py-20 lg:py-28 container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-[#77c8c7] px-3 py-1 rounded-full bg-[#77c8c7]/10 border border-[#77c8c7]/20">
            Customer Stories
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3">
            Trusted by Hundreds Across the Region
          </h2>
          <p className="text-slate-600 mt-3">
            Read how Velavan Finance helped customers achieve their emergency business and personal needs.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              name: "K. Ramesh Kumar",
              role: "Textile Business Owner",
              tag: "Gold Loan",
              review:
                "I needed urgent working capital for raw materials. Velavan Finance gave me the highest per-gram valuation and transferred ₹4,50,000 in just 25 minutes. Extremely professional!",
            },
            {
              name: "S. Murugan",
              role: "Commercial Driver",
              tag: "Vehicle Loan",
              review:
                "Got my vehicle loan approved smoothly with minimal paperwork. The EMI terms were tailored to my monthly earnings, making repayment stress-free.",
            },
            {
              name: "P. Meenakshi",
              role: "Homemaker & Investor",
              tag: "Chit Savings",
              review:
                "The transparency in their monthly chit scheme is remarkable. All statements are crystal clear and payout was prompt upon bidding. Highly recommended.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white rounded-3xl p-8 border border-slate-200/90 shadow-md hover:shadow-xl transition-shadow flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1 text-[#936097]">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#77c8c7] text-[#77c8c7]" />
                    ))}
                  </div>
                  <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#77c8c7]/15 text-[#0f766e] border border-[#77c8c7]/30">
                    {item.tag}
                  </span>
                </div>
                <p className="text-slate-700 text-sm leading-relaxed italic mb-6">
                  "{item.review}"
                </p>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <div className="font-bold text-slate-900">{item.name}</div>
                <div className="text-xs text-[#936097] font-semibold">{item.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= FAQ SECTION ================= */}
      <section className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#936097] px-3 py-1 rounded-full bg-[#936097]/10 border border-[#936097]/20">
              Got Questions?
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm transition-all"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full p-5 text-left flex justify-between items-center gap-4 font-bold text-slate-800 hover:text-[#936097] transition-colors cursor-pointer"
                  >
                    <span className="text-base sm:text-lg flex items-center gap-3">
                      <HelpCircle className="w-5 h-5 text-[#77c8c7] shrink-0" />
                      {faq.q}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${
                        isOpen ? "transform rotate-180 text-[#936097]" : ""
                      }`}
                    />
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5 pt-1 text-slate-600 text-sm sm:text-base leading-relaxed border-t border-slate-100">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= CLOSING CTA BANNER ================= */}
      <section className="py-20 container mx-auto px-6">
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-[#936097] via-[#855389] to-[#77c8c7] p-8 sm:p-14 text-white shadow-2xl">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full filter blur-[100px] opacity-15 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-white rounded-full filter blur-[100px] opacity-15 pointer-events-none" />

          <div className="relative z-10 max-w-2xl text-center sm:text-left">
            <span className="inline-block px-3.5 py-1 rounded-full bg-white/20 backdrop-blur-md text-white text-xs font-bold uppercase tracking-wider mb-3">
              Fast Track Approval
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4 text-white">
              Need Immediate Financial Support?
            </h2>
            <p className="text-white/90 text-base sm:text-lg mb-8 leading-relaxed font-normal">
              Connect with our dedicated loan advisors today or apply online in less than 5 minutes. We are here to help you move forward.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <button
                onClick={() => navigate("/loan-selection")}
                className="w-full sm:w-auto px-8 py-4 rounded-xl font-bold bg-white text-[#936097] hover:bg-slate-50 shadow-xl transition-all cursor-pointer transform hover:-translate-y-0.5"
              >
                Apply Online Now →
              </button>

              <button
                onClick={() => navigate("/contact")}
                className="w-full sm:w-auto px-8 py-4 rounded-xl font-semibold bg-white/15 hover:bg-white/25 border border-white/30 text-white backdrop-blur-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Mail className="w-4 h-4 text-white" />
                <span>Contact Us</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;